import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Chip from '@mui/material/Chip/Chip';
import Alert from '@mui/material/Alert/Alert';
import Avatar from '@mui/material/Avatar/Avatar';
import Checkbox from '@mui/material/Checkbox/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Snackbar from '@mui/material/Snackbar/Snackbar';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import { PageContainer } from '../../components/PageContainer';
import { Loading } from '../../components/Loading';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import { SessionDescription } from '../../components/Sessions/SessionDescription';
import Editor from '../../components/Editor/Editor';
import { useHackathon } from '../../hooks/useHackathon';
import { useSessions } from '../../hooks/useSessions';
import { useAuth } from '../../services/useAuth';
import { updateSession } from '../../services/session.service';
import { categoryLabels, roleLabels, sessionRoles } from '../../utils/sessions';
import { useLiveSessions } from '../../hooks/useLiveSessions';
import { SessionRunOfShow } from '../../components/Sessions/SessionRunOfShow';
import { SessionVotePanel } from '../../components/Sessions/SessionVotePanel';

dayjs.extend(relativeTime);

const RECENT_UPDATE_MS = 2 * 60 * 60 * 1000;

const Person = ({ person }: { person: { username: string, email: string } }) => (
    <Chip
        avatar={<Avatar>{(person.username || person.email || '?').charAt(0).toUpperCase()}</Avatar>}
        label={person.username || person.email}
        variant="outlined" />
)

export const SessionPage = () => {
    const { id, sessionId } = useParams();
    const { hackathon, loading } = useHackathon(id);
    const { sessions, loading: sessionsLoading, reload: reloadSessions } = useSessions(id);
    const live = useLiveSessions(id);
    const { user } = useAuth();

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [notify, setNotify] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const session = sessions.find(s => s.slug === sessionId || s._id === sessionId);

    // the live board polls every few seconds; when it shows newer details than we have, fetch them
    const liveDescriptionUpdated = live.find(s => s._id === session?._id)?.descriptionUpdatedAt?.getTime() || 0;
    const loadedDescriptionUpdated = session?.descriptionUpdatedAt?.getTime() || 0;
    useEffect(() => {
        if (liveDescriptionUpdated > loadedDescriptionUpdated && !editing) {
            reloadSessions();
        }
    }, [liveDescriptionUpdated, loadedDescriptionUpdated, editing, reloadSessions]);

    if ((!hackathon && loading) || (!session && sessionsLoading)) {
        return <Loading />
    }
    if (!hackathon || !id) {
        return <PageContainer><Alert severity="error">Hackathon not found</Alert></PageContainer>
    }
    if (!session) {
        return (
            <PageContainer>
                <Stack spacing={2}>
                    <Alert severity="error">This session doesn't exist. It may have been renamed or removed.</Alert>
                    <Box><Button component={Link} to={`/h/${hackathon.slug}#sessions`}>See all sessions</Button></Box>
                </Stack>
            </PageContainer>
        )
    }

    const email = user ? user.email : undefined;
    const isOrganizer = !!user && (user.isAdmin || hackathon.organizers.some(o => o.email === user.email));
    const roles = sessionRoles(session, hackathon, email);
    const canEdit = isOrganizer || roles.hosting;
    const updated = session.descriptionUpdatedAt;
    const recentlyUpdated = !!updated && Date.now() - updated.getTime() < RECENT_UPDATE_MS;
    const teams = session.ideas.map(ideaId => hackathon.ideas.find(i => i._id === ideaId)).filter(Boolean);

    const save = () => {
        setSaving(true);
        setError('');
        updateSession(id, session._id, { description: draft }, notify).then(({ notified }) => {
            setEditing(false);
            setMessage(notified > 0 ? `Saved, ${notified} ${notified === 1 ? 'person' : 'people'} notified` : 'Saved');
        }, err => setError(err.response?.data?.message || 'Could not save')).finally(() => setSaving(false));
    };

    return (
        <PageContainer variant="plain">
            <Stack spacing={3}>
                <AppBreadcrumbs crumbs={[
                    { type: PageType.Home, name: 'Home', link: '/' },
                    { type: PageType.Hackathon, name: hackathon.name, link: `/h/${hackathon.slug}` },
                    { type: PageType.Article, name: session.name }
                ]} />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'flex-end' }}>
                    <Stack spacing={0.5}>
                        <Typography variant="overline" color="text.secondary">Session</Typography>
                        <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '2.75rem' } }}>{session.name}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => {
                            navigator.clipboard?.writeText(window.location.href).then(() => setMessage('Link copied'), () => setMessage(window.location.href));
                        }}>Copy link</Button>
                        {isOrganizer && <Button variant="outlined" startIcon={<SettingsIcon />} component={Link} to={`/h/${hackathon.slug}/sessions`}>Manage sessions</Button>}
                    </Stack>
                </Stack>
                {
                    roleLabels(roles).length > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {roleLabels(roles).map(label => <Chip key={label} color="primary" label={`You're ${label.toLowerCase()} here`} />)}
                        </Stack>
                    )
                }

                <Box sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
                    alignItems: 'start'
                }}>
                    <Card>
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="h4" component="h2">Details</Typography>
                                        {
                                            updated && (
                                                <Chip
                                                    size="small"
                                                    color={recentlyUpdated ? 'secondary' : 'default'}
                                                    variant={recentlyUpdated ? 'filled' : 'outlined'}
                                                    label={`Updated ${dayjs(updated).fromNow()}`} />
                                            )
                                        }
                                    </Stack>
                                    {
                                        canEdit && !editing && (
                                            <Button size="small" startIcon={<EditIcon />} onClick={() => {
                                                setDraft(session.description);
                                                setNotify(true);
                                                setEditing(true);
                                            }}>Edit</Button>
                                        )
                                    }
                                </Stack>

                                {
                                    editing ? (
                                        <Stack spacing={2}>
                                            <React.Suspense fallback={<div>Loading...</div>}>
                                                <Editor toolbar={true} value={session.description} onChange={setDraft} />
                                            </React.Suspense>
                                            <FormControlLabel
                                                control={<Checkbox checked={notify} onChange={e => setNotify(e.target.checked)} />}
                                                label="Notify everyone in this session (judges, hosts and presenting teams)" />
                                            {error && <Alert severity="error">{error}</Alert>}
                                            <Stack direction="row" spacing={1}>
                                                <Button variant="contained" disabled={saving} onClick={save}>Save</Button>
                                                <Button disabled={saving} onClick={() => { setEditing(false); setError(''); }}>Cancel</Button>
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        session.description
                                            ? <SessionDescription markdown={session.description} />
                                            : <Typography color="text.secondary">No details yet{canEdit ? '. Add the meeting link or room so people know where to go.' : '.'}</Typography>
                                    )
                                }
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Stack spacing={2.5}>
                                <Stack spacing={1}>
                                    <Typography variant="overline" color="text.secondary">Judging categories</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {categoryLabels(hackathon, session.categories).map(label => <Chip key={label} color="primary" variant="outlined" label={label} />)}
                                        {session.categories.length === 0 && <Typography variant="body2" color="text.secondary">Not set yet</Typography>}
                                    </Stack>
                                </Stack>
                                <Stack spacing={1}>
                                    <Typography variant="overline" color="text.secondary">Judges</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {session.judges.map(j => <Person key={j._id} person={j} />)}
                                        {session.judges.length === 0 && <Typography variant="body2" color="text.secondary">Not assigned yet</Typography>}
                                    </Stack>
                                </Stack>
                                {
                                    session.hosts.length > 0 && (
                                        <Stack spacing={1}>
                                            <Typography variant="overline" color="text.secondary">Hosts</Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                {session.hosts.map(h => <Person key={h._id} person={h} />)}
                                            </Stack>
                                        </Stack>
                                    )
                                }
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>

                <SessionRunOfShow
                    hackathonId={id}
                    hackathon={hackathon}
                    session={session}
                    canRun={canEdit}
                    viewerEmail={email} />

                <SessionVotePanel
                    hackathonId={id}
                    hackathon={hackathon}
                    session={session}
                    viewerEmail={email} />
            </Stack>
            <Snackbar open={!!message} autoHideDuration={4000} onClose={() => setMessage('')} message={message} />
        </PageContainer>
    )
}
